import type { Transition } from "@deckloom/core/schema";

/**
 * Slide transitions as Web Animations on two stacked layers: `outgoing` shows the previous slide,
 * `incoming` the new one. Returns the animations so the caller can wait for them.
 * Directions are the direction of motion (OOXML `dir`), e.g. push "top" moves both slides upwards.
 */
export function runTransition(t: Transition, outgoing: HTMLElement, incoming: HTMLElement): Animation[] {
  const d = Math.max(50, t.duration);
  const ease = "ease-in-out";
  const anims: Animation[] = [];
  const run = (el: HTMLElement, frames: Keyframe[], opts: KeyframeAnimationOptions = {}) => {
    const a = el.animate(frames, { duration: d, easing: ease, fill: "forwards", ...opts });
    anims.push(a);
    return a;
  };
  const vec = (dir: Transition["direction"]): [number, number] => {
    switch (dir) {
      case "left": return [-1, 0];
      case "right": return [1, 0];
      case "top": return [0, -1];
      case "bottom": return [0, 1];
      case "topLeft": return [-1, -1];
      case "topRight": return [1, -1];
      case "bottomLeft": return [-1, 1];
      case "bottomRight": return [1, 1];
      default: return [-1, 0];
    }
  };
  switch (t.kind) {
    case "none":
    case "cut":
      return [];
    case "fadeThroughBlack": {
      // old slide fades to black, then the new one fades in from black
      outgoing.style.background = "#000";
      const inner = outgoing.firstElementChild as HTMLElement | null;
      if (inner) run(inner, [{ opacity: 1 }, { opacity: 0 }], { duration: d / 2 });
      run(outgoing, [{ opacity: 1 }, { opacity: 1 }, { opacity: 0 }], { duration: d, easing: "linear" });
      return anims;
    }
    case "push": {
      const [x, y] = vec(t.direction);
      run(outgoing, [{ transform: "translate(0,0)" }, { transform: `translate(${x * 100}%, ${y * 100}%)` }]);
      run(incoming, [{ transform: `translate(${-x * 100}%, ${-y * 100}%)` }, { transform: "translate(0,0)" }]);
      return anims;
    }
    case "cover": {
      const [x, y] = vec(t.direction);
      run(incoming, [{ transform: `translate(${-x * 100}%, ${-y * 100}%)` }, { transform: "translate(0,0)" }]);
      return anims;
    }
    case "uncover": {
      const [x, y] = vec(t.direction);
      run(outgoing, [{ transform: "translate(0,0)" }, { transform: `translate(${x * 100}%, ${y * 100}%)` }]);
      return anims;
    }
    case "wipe": {
      const from = t.direction === "right" ? "inset(0 100% 0 0)" : t.direction === "top" ? "inset(100% 0 0 0)" : t.direction === "bottom" ? "inset(0 0 100% 0)" : "inset(0 0 0 100%)";
      run(incoming, [{ clipPath: from }, { clipPath: "inset(0 0 0 0)" }], { easing: "linear" });
      return anims;
    }
    case "split": {
      const vertical = t.direction === "left" || t.direction === "right";
      run(incoming, [{ clipPath: vertical ? "inset(0 50% 0 50%)" : "inset(50% 0 50% 0)" }, { clipPath: "inset(0 0 0 0)" }], { easing: "linear" });
      return anims;
    }
    case "zoom":
      run(incoming, [{ transform: "scale(0.6)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }]);
      return anims;
    case "fade":
    case "fallback":
    default:
      run(outgoing, [{ opacity: 1 }, { opacity: 0 }], { easing: "linear" });
      return anims;
  }
}

/** Which layer must be stacked on top for the transition to look right. */
export function incomingOnTop(kind: Transition["kind"]): boolean {
  return kind === "cover" || kind === "wipe" || kind === "split" || kind === "zoom";
}
